import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Brain, Moon, Wind, ChevronRight, Timer } from 'lucide-react';
import PostCard from '../components/PostCard';
import { useContent } from '../context/ContentContext';
import { useAuth } from '../context/AuthContext';
import { useContentByType, shapeDbToPost } from '../data/contentBridge';
import { fetchFlagshipProgram } from '../lib/programsApi';

// The seven ~5-minute "7 Days to Breathe Again" recordings also live in the Meditate tab's 5-Minute filter.
const FIVE_MIN_PROGRAM_SLUG = '7-days-to-breathe-again';
const useFiveMinuteProgramTracks = (enabled) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchFlagshipProgram(FIVE_MIN_PROGRAM_SLUG)
      .then((d) => {
        if (cancelled) return;
        const title = d?.program?.title || '7 Days to Breathe Again';
        const label = d?.program?.dayLabel || 'Day';
        setItems((d?.days || []).filter((day) => !day.archived && day.primary).map((day) => ({
          ...shapeDbToPost(day.primary),
          image: day.primary.coverImageUrl || d.program?.coverImageUrl || null,
          excerpt: `${label} ${day.dayNumber} · ${title}`,
          tags: [...new Set([...(day.primary.tags || []), '5-min', 'program'])],
          programSlug: FIVE_MIN_PROGRAM_SLUG,
          programDay: day.dayNumber,
        })));
      })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [enabled]);
  return items;
};

// Category slugs → DB content_type. Keep these in sync with backend
// VALID_CONTENT_TYPE. Any slug missing here falls through to mockData.
const CATEGORY_TO_TYPE = {
  'guided-meditations': 'meditation',
  affirmations:         'affirmation',
  sleep:                'sleep_meditation',
  anxiety:              'reflection',
};

const iconMap = {
  Sparkles: Sparkles,
  Brain: Brain,
  Moon: Moon,
  Wind: Wind,
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { categories, breathingGroups, getPostsByCategory, getCategoryName } = useContent();
  const mappedType = CATEGORY_TO_TYPE[categoryId];
  // Content bridge — if the DB has records for this type, use them; else
  // fall back to the existing mockData. `_source` is exposed for the
  // debug badge below so we can visually confirm which layer is serving.
  const { items: dbItems, source: bridgeSource } = useContentByType(mappedType || '__none__');
  // Guided Meditations also lists library videos filed under that category (e.g. Deep Breathing Meditation).
  const { items: videoItems } = useContentByType(categoryId === 'guided-meditations' ? 'video' : '__none__');
  const guidedVideos = useMemo(() => videoItems.filter((v) => {
    const cat = String(v.category || '').toLowerCase();
    const tags = (v.tags || []).map((t) => String(t).toLowerCase());
    return v.videoUrl && (cat.includes('guided') || tags.includes('guided meditations'));
  }), [videoItems]);
  const programTracks = useFiveMinuteProgramTracks(categoryId === 'guided-meditations');
  const posts = useMemo(() => {
    const db = (mappedType && bridgeSource === 'db' && dbItems.length > 0) ? dbItems : null;
    if (!db && guidedVideos.length === 0 && programTracks.length === 0) return getPostsByCategory(categoryId);
    const seen = new Set(programTracks.map((t) => t.id));
    return [...programTracks, ...(db || []).filter((x) => !seen.has(x.id)), ...guidedVideos];
  }, [mappedType, bridgeSource, dbItems, guidedVideos, programTracks, categoryId, getPostsByCategory]);
  const categoryName = getCategoryName(categoryId);
  const category = categories.find((c) => c.id === categoryId);
  const IconComponent = category ? iconMap[category.icon] : Sparkles;
  const { user, visitCategory, visitedCategories } = useAuth();
  const isAnxiety = categoryId === 'anxiety';
  const isMeditations = categoryId === 'guided-meditations';
  // Sub-tab filter for the meditations category — currently "All" and
  // "5-Minute Meditations". Adding more short-form tabs later is a one-line
  // change to `MEDITATION_TABS`.
  const [meditationTab, setMeditationTab] = useState('all');
  const MEDITATION_TABS = [
    { id: 'all',   label: 'All' },
    { id: '5-min', label: '5-Minute' },
  ];
  const filteredPosts = useMemo(() => {
    if (!isMeditations || meditationTab === 'all') return posts;
    return posts.filter((p) => {
      const tags = (p.tags || []).map((t) => String(t).toLowerCase());
      if (tags.includes(meditationTab)) return true;
      // Fallback: parse duration text like "5 min" so we don't have to
      // touch every existing meditation to tag it.
      if (meditationTab === '5-min') {
        const m = String(p.duration || '').match(/\d+/);
        return !!m && parseInt(m[0], 10) <= 5;
      }
      return false;
    });
  }, [isMeditations, meditationTab, posts]);
  // Sleep gets a full palette shift into midnight blue with muted sage/lavender
  // accents so the user visually feels they've entered a different mode.
  const isSleep = categoryId === 'sleep';
  const totalBreathing = isAnxiety
    ? breathingGroups.reduce((s, g) => s + g.exercises.length, 0)
    : 0;

  // Sleep mode: paint the whole viewport midnight blue by dropping a fixed
  // background behind everything (so it extends past the app-shell frame too).
  useEffect(() => {
    if (!isSleep) return undefined;
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#152638';
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, [isSleep]);

  // Scroll to top only when the category changes, not when user state mutates.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId]);

  // Record the visit at most once per user per category.
  useEffect(() => {
    if (user && !visitedCategories.includes(categoryId)) {
      visitCategory(categoryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, user?.id]);

  return (
    <div
      className="animate-fade-in"
      style={
        isSleep
          ? {
              background:
                'linear-gradient(180deg, #152638 0%, #1a2f47 50%, #152638 100%)',
              minHeight: '100vh',
              color: '#E4DEF0',
            }
          : undefined
      }
      data-sleep-mode={isSleep ? 'true' : undefined}
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-6">
        <button
          onClick={() => navigate('/')}
          className={`flex items-center gap-2 mb-6 transition-colors duration-300 ${
            isSleep
              ? 'text-[#A5A2C4] hover:text-[#D6AD60]'
              : 'text-stone-500 hover:text-[#D6AD60]'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        <div className="flex items-center gap-4">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${
              isSleep ? '' : 'btn-obsidian'
            }`}
            style={
              isSleep
                ? {
                    background: 'linear-gradient(135deg, #2b3d5c 0%, #1e2d47 100%)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(165,162,196,0.25)',
                  }
                : undefined
            }
          >
            <IconComponent
              className="w-7 h-7"
              style={isSleep ? { color: '#C9C4E8' } : undefined}
            />
          </div>
          <div>
            <h1
              className="text-2xl font-bold"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: isSleep ? '#F0EDF7' : '#242824',
              }}
            >
              {categoryName}
            </h1>
            <p
              className="text-sm"
              style={{ color: isSleep ? '#A5A2C4' : undefined }}
            >
              {posts.length} {posts.length === 1 ? 'reflection' : 'reflections'}
            </p>
          </div>
        </div>
      </div>

      {/* Breathing Exercises Banner — Anxiety only */}
      {isAnxiety && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <Link
            to="/breathing"
            className="relative block rounded-3xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 group"
            data-testid="breathing-banner"
            style={{
              background:
                'linear-gradient(135deg, #1c1917 0%, #292524 45%, #44403c 100%)',
            }}
          >
            {/* Animated orbs in background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute -right-8 -top-8 w-40 h-40 rounded-full"
                style={{
                  background: 'radial-gradient(circle, #D6AD60 0%, transparent 70%)',
                  opacity: 0.35,
                  animation: 'breath-banner 6s ease-in-out infinite',
                }}
              />
              <div
                className="absolute -left-10 -bottom-10 w-32 h-32 rounded-full"
                style={{
                  background: 'radial-gradient(circle, #34d399 0%, transparent 70%)',
                  opacity: 0.25,
                  animation: 'breath-banner 8s ease-in-out infinite 1s',
                }}
              />
            </div>
            <div className="relative p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Wind className="w-7 h-7 text-[#D6AD60]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D6AD60] mb-1">
                  Interactive · Animated
                </p>
                <h2
                  className="text-lg font-semibold text-white leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {totalBreathing} Breathing Exercises
                </h2>
                <p className="text-xs text-white/60 mt-0.5 line-clamp-1">
                  Circle timers across 6 categories
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60 group-hover:translate-x-0.5 group-hover:text-white transition-all" />
            </div>
            <style>{`
              @keyframes breath-banner {
                0%, 100% { transform: scale(0.85); }
                50% { transform: scale(1.15); }
              }
            `}</style>
          </Link>
        </div>
      )}
      {posts.length > 0 ? (
        <div className="px-5 pb-8">
          {isMeditations && (
            <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="meditation-tabs">
              {MEDITATION_TABS.map((t) => {
                const active = meditationTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setMeditationTab(t.id)}
                    data-testid={`meditation-tab-${t.id}`}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full flex-shrink-0 transition-all active:scale-95 ${
                      active
                        ? 'bg-[#D6AD60] text-[#242824]'
                        : 'bg-white/10 text-white/80 border border-white/25 hover:bg-white/20'
                    }`}
                  >
                    {t.id === '5-min' && <Timer className="w-3 h-3" />}
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
          {filteredPosts.length > 0 ? (
            <div className="grid grid-cols-2 gap-4">
              {filteredPosts.map((post, index) => (
                <div
                  key={post.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <PostCard post={post} variant="grid" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Timer className="w-8 h-8 text-stone-500/50 mb-3" />
              <p className="text-stone-500 text-center text-sm">
                No meditations in this tab yet — check back soon.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 px-5">
          <IconComponent className="w-12 h-12 text-stone-500/50 mb-4" />
          <p className="text-stone-500 text-center">
            No reflections in this category yet
          </p>
        </div>
      )}
    </div>
  );
};

export default CategoryPage;
